import React from 'react';
import { useState, useEffect, useRef } from 'react';
import debounce from 'lodash.debounce';
import * as authentication from '../util/authentication';
import AutoComplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';


import request from '@nineeng/request';

const loadByValues = async (dataSource, value) => {
	let ids = Array.isArray(value) ? value.join(',') : value;
	const authToken = authentication.extractAuthorization();
	const authParams = authentication.extractAuthParams();
	const { body } = await request.get(`${dataSource}/${ids}?${authParams}`, { headers: { Authorization: authToken } });
	return body;
};

const loadByQuery = async ({ dataSource, query = '', filter = '', instantSearch }) => {
	const url = dataSource;
	const urlSearchParams = new URLSearchParams();
	if (query) urlSearchParams.append('q', query);
	if (instantSearch) urlSearchParams.append('instantSearch', instantSearch);
	if (filter) {
		urlSearchParams.append(filter.key, Array.isArray(filter.value) ? filter.value.join(',') : filter.value);
	}
	const authToken = authentication.extractAuthorization();
	const authParams = authentication.extractAuthParams();
	const { body } = await request.get(`${url}?${urlSearchParams.toString()}&${authParams}`, { headers: { Authorization: authToken } });
	return body;
};

const AsyncAutoComplete = ({
	name,
	value,
	values,
	label,
	multiple = false,
	onChange,
	dataSource,
	filter,
	required,
	helperText,
	error,
	placeholder,
	instantSearch,
	returnFullObject = false,
	disabled = false,
	...props
}) => {
	const [loading, setLoading] = useState(true);
	const [options, setOptions] = useState([]);
	const [internalValues, setInternalValues] = useState(multiple ? [] : null);
	const [searchValue, setSearchValue] = useState('');

	const debouncedSearch = useRef(
		debounce(async (searchValue, values) => {
			try {
				const queryFilter = filter ? ({ key: filter, value: values[filter] }) : '';
				const data = await loadByQuery({ dataSource, query: searchValue, filter: queryFilter, instantSearch });
				setOptions(data);
			} catch (e) {
				//TODO: status update here
			} finally {
				setLoading(false);
			}
		}, 200)).current;


	useEffect(() => {
		if (!value || (Array.isArray(value) && value.length === 0)) {
			setInternalValues(multiple ? [] : null);
			return setLoading(false);
		}

		const loadData = async () => {
			setLoading(true);
			const newOptions = await loadByValues(dataSource, value);
			if (multiple) {
				setInternalValues(newOptions);
			} else {
				setInternalValues(newOptions[0] || null);
			}
			//setOptions([...options, newOptions]);
			setLoading(false);
		};

		loadData();
	}, [value, dataSource, multiple]);

	const openHandler = async () => {
		setLoading(true);
		await debouncedSearch(searchValue || '', values);
		setLoading(false);
	};
	// TODO check if necessary
	const closeHandler = () => { };
	const onChangeHandler = (e, internalValue) => {
		const returnValue = returnFullObject 
			? internalValue 
			: (multiple ? (internalValue || []).map(v => v.value) : internalValue?.value);
		
		onChange({ target: { name, value: returnValue } });
		setInternalValues(internalValue);
	};

	const onInputChangeHandler = (event, inputValue) => {
		setLoading(true);
		setOptions([]);
		setSearchValue(inputValue);
		debouncedSearch(inputValue, values);
	};

	const amountOfCharactersUntilSearchIsTriggered = 3 - searchValue.length;

	return <AutoComplete
		clearOnBlur={false}
		fullWidth
		multiple={multiple}
		options={options}
		onOpen={openHandler}
		limitTags={3}
		disabled={disabled}
		onChange={onChangeHandler}
		onInputChange={onInputChangeHandler}
		renderTags={(tags, getTagProps) => (
			<Box sx={{ maxWidth: '100%' }}>
				{tags.map((tag, index) => {
					const tagProps = getTagProps({ index });
					const { key, onDelete } = tagProps;
					return (
						<Chip key={key} label={tag.label} style={{ marginRight: 4 }} title={tag.label} onDelete={disabled ? undefined : onDelete}/>
					);
				})}
			</Box>
		)}
		onClose={closeHandler}
		loading={loading}
		value={internalValues}
		getOptionLabel={(d) => d.label}
		filterSelectedOptions
		// to enable remote searching
		filterOptions={(x) => x}
		isOptionEqualToValue={(o, v) => o.value === v.value}
		noOptionsText={
			searchValue?.length > 2 || instantSearch
				? 'No options'
				: `${amountOfCharactersUntilSearchIsTriggered} more character${amountOfCharactersUntilSearchIsTriggered === 1 ? '' : 's'} to search…`
		}
		{...props}
		renderInput={(params) => (
			<TextField
				sx={{ marginBottom: 2 }}
				{...params}
				ref={params.inputProps.ref}
				label={label}
				required={required}
				helperText={helperText}
				error={error}
				inputProps={params.inputProps}
				placeholder={placeholder}
			/>
		)}
	/>;
};

AsyncAutoComplete.type = 'AsyncAutoComplete';

export default AsyncAutoComplete;
